import React, { useState, useRef } from 'react';
import { Card, Button, Alert, Form, Modal, Row, Col } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { generateUUID, sanitizeInfoHTML } from '../shared/utils';
import JoditEditor from 'jodit-react';
import './InfoTab.css';

const InfoTab = ({ parsedData, onUpdatePlan }) => {
  const { t } = useTranslation();
  
  // State variables
  const [infoSections, setInfoSections] = useState(parsedData.infoSections || []);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [currentSection, setCurrentSection] = useState(null);
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [error, setError] = useState('');
  const editor = useRef(null);
  const joditInstance = useRef(null);

  // Jodit Editor configuration
  const config = {
    readonly: false,
    height: 400,
    enableDragAndDropFileToEditor: true,
    uploader: {
      insertImageAsBase64URI: true
    },
    buttons: [
      'source', '|',
      'bold', 'italic', 'underline', 'strikethrough', '|',
      'font', 'fontsize', 'brush', 'paragraph', '|',
      'ul', 'ol', '|',
      'table', 'link', 'image', '|',
      'align', '|',
      'undo', 'redo', '|',
      'hr', 'eraser', 'copyformat', '|',
      'fullsize'
    ],
    buttonsXS: [
      'bold', 'italic', '|',
      'paragraph', '|',
      'ul', 'ol', '|',
      'link', 'image'
    ],
    cleanHTML: {
      timeout: 0,
      removeEmptyElements: false
    },
    colors: {
      greyscale: ['#000000', '#434343', '#666666', '#999999', '#B7B7B7', '#CCCCCC', '#D9D9D9', '#EFEFEF', '#F3F3F3', '#FFFFFF'],
      palette: ['#980000', '#FF0000', '#FF9900', '#FFFF00', '#00F0F0', '#00FFFF', '#4A86E8', '#0000FF', '#9900FF', '#FF00FF'],
      full: [
        '#E6B8AF', '#F4CCCC', '#FCE5CD', '#FFF2CC', '#D9EAD3', '#D0E0E3', '#C9DAF8', '#CFE2F3', '#D9D2E9', '#EAD1DC',
        '#DD7E6B', '#EA9999', '#F9CB9C', '#FFE599', '#B6D7A8', '#A2C4C9', '#A4C2F4', '#9FC5E8', '#B4A7D6', '#D5A6BD',
        '#CC4125', '#E06666', '#F6B26B', '#FFD966', '#93C47D', '#76A5AF', '#6D9EEB', '#6FA8DC', '#8E7CC3', '#C27BA0',
        '#A61C00', '#CC0000', '#E69138', '#F1C232', '#6AA84F', '#45818E', '#3C78D8', '#3D85C6', '#674EA7', '#A64D79',
        '#85200C', '#990000', '#B45F06', '#BF9000', '#38761D', '#134F5C', '#1155CC', '#0B5394', '#351C75', '#741B47',
        '#5B0F00', '#660000', '#783F04', '#7F6000', '#274E13', '#0C343D', '#1C4587', '#073763', '#20124D', '#4C1130'
      ]
    },
    language: 'en',
    toolbarSticky: true,
    toolbarStickyOffset: 0,
    iframe: false,
    disablePlugins: 'about',
    removeButtons: ['about'],
    showCharsCounter: false,
    showWordsCounter: false,
    showXPathInStatusbar: false,
    askBeforePasteHTML: false,
    askBeforePasteFromWord: false,
    defaultActionOnPaste: 'insert_clear_html',
    placeholder: t('planDetail.info.startTyping'),
    spellcheck: true,
    theme: 'default',
    events: {
      afterInit: (instance) => {
        joditInstance.current = instance;
      }
    }
  };

  // Handle input change for title
  const handleTitleChange = (e) => {
    setTitle(e.target.value);
  };

  // We're not using onChange anymore to prevent jumps
  const handleContentChange = () => {
    // Intentionally empty - we'll get content only when saving
  };

  // Get the current content directly from Jodit instance
  const getCurrentEditorContent = () => {
    return joditInstance.current ? joditInstance.current.value : content;
  };

  // Open add section modal
  const handleAddSection = () => {
    setTitle('');
    setContent('');
    setError('');
    setShowAddModal(true);
  };

  // Open edit section modal
  const handleEditSection = (section) => {
    setTitle(section.title);
    setContent(section.content || '');
    setCurrentSection(section);
    setError('');
    setShowEditModal(true);
  };

  // Save new section
  const handleSaveNewSection = () => {
    if (!title.trim()) {
      setError(t('planDetail.info.titleRequired'));
      return;
    }

    // Get content directly from the editor instance and sanitize it
    const editorContent = getCurrentEditorContent();
    const sanitizedContent = sanitizeInfoHTML(editorContent);

    const newSection = {
      id: generateUUID(),
      title: title.trim(),
      content: sanitizedContent,
      createdAt: new Date().toISOString()
    };

    const updatedSections = [...infoSections, newSection];
    setInfoSections(updatedSections);
    
    // Save to plan data
    const updatedData = {
      ...parsedData,
      infoSections: updatedSections
    };
    onUpdatePlan(updatedData);
    
    setShowAddModal(false);
  };

  // Update existing section
  const handleUpdateSection = () => {
    if (!title.trim()) {
      setError(t('planDetail.info.titleRequired'));
      return;
    }

    // Get content directly from the editor instance and sanitize it
    const editorContent = getCurrentEditorContent();
    const sanitizedContent = sanitizeInfoHTML(editorContent);

    const updatedSections = infoSections.map(section => 
      section.id === currentSection.id 
        ? { 
            ...section, 
            title: title.trim(), 
            content: sanitizedContent,
            updatedAt: new Date().toISOString()
          } 
        : section
    );

    setInfoSections(updatedSections);
    
    // Save to plan data
    const updatedData = {
      ...parsedData,
      infoSections: updatedSections
    };
    onUpdatePlan(updatedData);
    
    setShowEditModal(false);
  };

  // Delete section
  const handleDeleteSection = (sectionId) => {
    if (window.confirm(t('planDetail.info.deleteConfirm'))) {
      const updatedSections = infoSections.filter(section => section.id !== sectionId);
      setInfoSections(updatedSections);
      
      // Save to plan data
      const updatedData = {
        ...parsedData,
        infoSections: updatedSections
      };
      onUpdatePlan(updatedData);
    }
  };

  // Handle modal close
  const handleCloseModal = () => {
    setShowAddModal(false);
    setShowEditModal(false);
  };

  return (
    <div className="info-tab py-3">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h4 className="mb-0">{t('planDetail.info.tripInformation')}</h4>
        <Button variant="primary" onClick={handleAddSection}>
          <i className="bi bi-plus-circle me-2"></i>
          {t('planDetail.info.addInformation')}
        </Button>
      </div>

      {infoSections.length === 0 ? (
        <Alert variant="light" className="text-center py-5">
          <i className="bi bi-info-circle display-4 d-block mb-3 text-muted"></i>
          <p>{t('planDetail.info.noSectionsYet')}</p>
          <p className="mb-0">{t('planDetail.info.addSectionsDescription')}</p>
        </Alert>
      ) : (
        <Row>
          {infoSections.map(section => (
            <Col md={12} key={section.id} className="mb-4">
              <Card>
                <Card.Header className="d-flex justify-content-between align-items-center">
                  <h5 className="mb-0">{section.title}</h5>
                  <div>
                    <Button 
                      variant="outline-primary" 
                      size="sm" 
                      className="me-2"
                      onClick={() => handleEditSection(section)}
                    >
                      <i className="bi bi-pencil"></i>
                    </Button>
                    <Button 
                      variant="outline-danger" 
                      size="sm"
                      onClick={() => handleDeleteSection(section.id)}
                    >
                      <i className="bi bi-trash"></i>
                    </Button>
                  </div>
                </Card.Header>
                <Card.Body>
                  <div className="info-content" dangerouslySetInnerHTML={{ __html: sanitizeInfoHTML(section.content) }} />
                </Card.Body>
              </Card>
            </Col>
          ))}
        </Row>
      )}

      {/* Add Section Modal */}
      <Modal show={showAddModal} onHide={handleCloseModal} centered size="lg">
        <Modal.Header closeButton>
          <Modal.Title>{t('planDetail.info.addSection')}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {error && <Alert variant="danger">{error}</Alert>}
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>{t('planDetail.info.sectionTitle')}</Form.Label>
              <Form.Control 
                type="text" 
                value={title} 
                onChange={handleTitleChange}
                placeholder={t('planDetail.info.sectionTitlePlaceholder')}
              />
            </Form.Group>
            
            <Form.Group className="mb-3">
              <Form.Label>{t('planDetail.info.content')}</Form.Label>
              <div className="editor-container">
                <JoditEditor
                  ref={editor}
                  value={content}
                  config={config}
                  onChange={handleContentChange}
                />
              </div>
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseModal}>{t('planDetail.info.cancel')}</Button>
          <Button variant="primary" onClick={handleSaveNewSection}>{t('planDetail.info.saveSection')}</Button>
        </Modal.Footer>
      </Modal>

      {/* Edit Section Modal */}
      <Modal show={showEditModal} onHide={handleCloseModal} centered size="lg">
        <Modal.Header closeButton>
          <Modal.Title>{t('planDetail.info.editSection')}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {error && <Alert variant="danger">{error}</Alert>}
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>{t('planDetail.info.sectionTitle')}</Form.Label>
              <Form.Control 
                type="text" 
                value={title} 
                onChange={handleTitleChange}
              />
            </Form.Group>
            
            <Form.Group className="mb-3">
              <Form.Label>{t('planDetail.info.content')}</Form.Label>
              <div className="editor-container">
                <JoditEditor
                  ref={editor}
                  value={content}
                  config={config}
                  onChange={handleContentChange}
                />
              </div>
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseModal}>{t('planDetail.info.cancel')}</Button>
          <Button variant="primary" onClick={handleUpdateSection}>{t('planDetail.info.updateSection')}</Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default InfoTab; 